import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { requireData, TradingEngineError } from "../../shared/api/api-error.js";
import { useApi } from "../../shared/api/api-context.js";
import type { components } from "../../shared/api/generated/trading-engine.js";
import type { BrokerAccountView } from "../settings/models.js";
import type { PositionView } from "./models.js";
import { positionKeys } from "./queries.js";

type Preview = components["schemas"]["ManualOrderPreview"];
type ManualOrder = components["schemas"]["ManualOrder"];
type Availability = "checking" | "enabled" | "disabled" | "unavailable";

function errorText(value: unknown): string {
  if (value instanceof TradingEngineError) {
    const details = value.details as { brokerRejection?: { code?: unknown } } | undefined;
    const code = details?.brokerRejection?.code;
    return typeof code === "string" && /^[a-z][a-z0-9_-]{0,63}$/iu.test(code)
      ? `${value.message} (${code})`
      : value.message;
  }
  return value instanceof Error
    ? value.message
    : "Request failed. Check order status before retrying.";
}

function disabledReason(
  position: PositionView,
  account: BrokerAccountView | undefined,
  availability: Availability,
): string | null {
  if (availability === "checking") return "Checking manual trading availability.";
  if (availability === "unavailable") return "Manual trading availability could not be verified.";
  if (availability === "disabled") return "Manual trading is disabled for this environment.";
  if (position.source !== "broker" || position.reconciliation !== "confirmed") {
    return "Position must be broker-confirmed before it can be closed here.";
  }
  const quantity = Math.abs(Number(position.quantity));
  if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 1_000) {
    return "Only whole-share positions of up to 1,000 shares can be closed here.";
  }
  if (account?.status !== "ready" || !account.capabilities.includes("stocks")) {
    return "A ready stock-trading account is required.";
  }
  return null;
}

export function ClosePositionControl({
  account,
  availability,
  position,
}: {
  readonly account: BrokerAccountView | undefined;
  readonly availability: Availability;
  readonly position: PositionView;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const reason = disabledReason(position, account, availability);
  const quantity = Math.abs(Number(position.quantity));
  const action = position.side === "long" ? "sell-to-close" : "buy-to-close";
  const previewMatchesPosition =
    preview !== null &&
    reason === null &&
    preview.accountId === position.brokerAccountId &&
    preview.symbol === position.symbol &&
    preview.action === action &&
    Number(preview.positionQuantity) === Number(position.quantity) &&
    preview.quantity === quantity;

  const previewMutation = useMutation({
    mutationFn: async () =>
      requireData(
        await api.POST("/manual-orders/preview", {
          body: {
            accountId: position.brokerAccountId,
            symbol: position.symbol,
            action,
            closeEntirePosition: true,
            quantity,
          },
        }),
      ),
    onSuccess: (result) => {
      setPreview(result);
      setConfirmed(false);
    },
  });
  const submitMutation = useMutation({
    mutationFn: async (value: Preview): Promise<ManualOrder> =>
      requireData(
        await api.POST("/manual-orders/{previewId}/submit", {
          params: { path: { previewId: value.id } },
        }),
      ),
    onSuccess: async (result) => {
      setOrderId(result.id);
      setPreview(null);
      setConfirmed(false);
      await queryClient.invalidateQueries({ queryKey: positionKeys.all });
    },
  });
  const order = useQuery({
    queryKey: ["manual-order", orderId],
    enabled: orderId !== null,
    queryFn: async ({ signal }) => {
      const result = requireData(
        await api.GET("/manual-orders/{orderId}", {
          params: { path: { orderId: orderId ?? "" } },
          signal,
        }),
      );
      if (result.status === "filled") {
        await queryClient.invalidateQueries({ queryKey: positionKeys.all });
      }
      return result;
    },
    refetchInterval: (query) =>
      query.state.data !== undefined &&
      ["filled", "cancelled", "rejected", "not-submitted"].includes(query.state.data.status)
        ? false
        : 5_000,
  });

  return (
    <div className="position-close-control">
      <button
        className="button"
        disabled={
          reason !== null ||
          previewMutation.isPending ||
          submitMutation.isPending ||
          orderId !== null ||
          submitMutation.error !== null
        }
        onClick={() => {
          setPreview(null);
          setConfirmed(false);
          previewMutation.mutate();
        }}
        type="button"
      >
        {previewMutation.isPending ? "Checking broker…" : "Close Position"}
      </button>
      {reason !== null ? <p className="section-description">{reason}</p> : null}
      {position.stale && reason === null ? (
        <p className="section-description">
          Displayed position is stale; the broker will be checked before a close is offered.
        </p>
      ) : null}
      {previewMutation.error !== null ? (
        <p role="alert">{errorText(previewMutation.error)}</p>
      ) : null}
      {preview !== null && !previewMatchesPosition ? (
        <p role="alert">Position changed. Request a fresh close preview.</p>
      ) : null}
      {previewMatchesPosition && preview !== null ? (
        <div
          className="manual-order-confirmation"
          role="group"
          aria-label={`Confirm close ${position.symbol}`}
        >
          <h3>Confirm full close</h3>
          <p>
            {preview.action === "buy-to-close" ? "Buy to Close" : "Sell to Close"}{" "}
            {preview.quantity} {preview.symbol} shares in{" "}
            {account?.displayName ?? preview.accountId}.
          </p>
          <p>
            Broker position: {preview.positionQuantity} shares. Indicative broker quote: $
            {preview.indicativePriceUsd}; estimated notional: ${preview.estimatedNotionalUsd}.
          </p>
          <p>
            Quote at {new Date(preview.quoteAt).toLocaleTimeString()}. Preview expires{" "}
            {new Date(preview.expiresAt).toLocaleTimeString()}.
          </p>
          <p>{preview.warning}</p>
          {preview.brokerWarnings.length > 0 ? (
            <div role="alert">
              <strong>Tastytrade preflight warnings</strong>
              <ul>
                {preview.brokerWarnings.map((warning, index) => (
                  <li key={`${warning.code ?? "warning"}-${index}`}>
                    {warning.message}
                    {warning.code === undefined ? "" : ` (${warning.code})`}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <label className="manual-order-confirmation__check">
            <input
              checked={confirmed}
              onChange={(event) => setConfirmed(event.currentTarget.checked)}
              type="checkbox"
            />{" "}
            I confirm this live Tastytrade market order closes the entire {preview.symbol} position
            {preview.brokerWarnings.length > 0 ? " and have reviewed the broker warnings" : ""}.
          </label>
          <div className="button-row">
            <button
              className="button"
              disabled={!confirmed || submitMutation.isPending}
              onClick={() => {
                if (Date.parse(preview.expiresAt) <= Date.now()) {
                  setPreview(null);
                  return;
                }
                submitMutation.mutate(preview);
              }}
              type="button"
            >
              {submitMutation.isPending ? "Submitting once…" : "Submit live close order"}
            </button>
            <button className="button button--quiet" onClick={() => setPreview(null)} type="button">
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {submitMutation.error !== null ? (
        <p role="alert">
          {errorText(submitMutation.error)} If submission is uncertain, check History and Tastytrade
          before trying again.
        </p>
      ) : null}
      {orderId !== null ? (
        <div role="status">
          <strong>Order {orderId}</strong>: {order.data?.status ?? "Checking broker status…"}
          {order.data?.status === "unknown" ? (
            <p>Broker outcome is uncertain. Do not submit another close order.</p>
          ) : null}
          {order.data?.status === "not-submitted" ? (
            <p>Tastytrade preflight failed. No live close order was sent.</p>
          ) : null}
          {order.error !== null ? (
            <p>Broker status could not be refreshed. Do not resubmit until it is reconciled.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
